import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { extractReactiveValue, type MaybeReactive } from '@/utils/reactive-helpers'
import { queryClient } from './vue-query'

/**
 * Unified query configurations for consistent caching strategies
 * Following domain-driven design principles
 */
export const QUERY_CONFIGS = {
  // Real-time data (mining stats, worker status)
  REAL_TIME: {
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
    retry: 2
  },
  
  // Frequent updates (account overview, payment settings)
  FREQUENT: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2
  },
  
  // Stable data (user info, static settings)
  STABLE: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 2
  },
  
  // External APIs (crypto prices)
  EXTERNAL: {
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
    retry: 1 // Less retries for external APIs
  },
  
  // Historical data (rarely changes)
  HISTORICAL: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1
  }
} as const

/**
 * Smart retry factory with configurable exclusion codes
 * Prevents retry on authentication and validation errors
 */
export const createSmartRetry = (
  maxRetries = 2,
  excludeCodes = [2000, 13000, 13001, 13002, 13003, 13004, 13005]
) => {
  return (failureCount: number, error: unknown) => {
    // Don't retry on specific error codes
    if (error && typeof error === 'object' && 'code' in error) {
      const errorCode = (error as any).code
      if (excludeCodes.includes(errorCode)) {
        return false
      }
    }
    
    // Don't retry on 4xx client errors for external APIs
    if (error && typeof error === 'object' && 'originalError' in error) {
      const axiosError = (error as any).originalError
      if (axiosError?.response?.status >= 400 && axiosError?.response?.status < 500) {
        return false
      }
    }
    
    return failureCount < maxRetries
  }
}

/**
 * Batch reactive parameter processor
 * Eliminates verbose individual reactive computations
 */
export const createReactiveParams = <T extends Record<string, any>>(
  params: { [K in keyof T]: MaybeReactive<T[K]> }
): ComputedRef<T> => {
  return computed(() => {
    const result = {} as T
    for (const [key, value] of Object.entries(params)) {
      result[key as keyof T] = extractReactiveValue(value)
    }
    return result
  })
}

/**
 * Query key factory for consistent naming
 * Prevents typos and ensures predictable cache keys
 */
export const createQueryKey = {
  user: {
    info: () => ['user', 'info'] as const,
    overview: (coin?: string) => ['user', 'account-overview', coin] as const,
    workers: (coin?: string) => ['user', 'workers', { coin }] as const,
    workerStats: (params: Record<string, any>) => ['user', 'workers-stats', params] as const,
    hashrate: (params: Record<string, any>) => ['user', 'worker-hashrate', params] as const,
    hashrateHistory: (params: Record<string, any>) => ['user', 'hashrate-history', params] as const,
    hashrateChart: (params: Record<string, any>) => ['user', 'hashrate-chart', params] as const,
    workerOverview: (params: Record<string, any>) => ['user', 'worker-overview', params] as const,
    workerHashrateHistory: (params: Record<string, any>) => ['user', 'worker-hashrate-history', params] as const,
    rewards: (params: Record<string, any>) => ['user', 'rewards', params] as const
  },
  wallet: {
    settings: (coin?: string) => ['wallet', 'settings', coin] as const,
    paymentHistory: (params: Record<string, any>) => ['wallet', 'payment-history', params] as const
  },
  pool: {
    metrics: (coin?: string) => ['pool', 'metrics', coin] as const,
    hashrateChart: (params: Record<string, any>) => ['pool', 'hashrate-chart', params] as const,
    foundBlocks: (params?: Record<string, any>) => ['pool', 'found-blocks', params] as const
  },
  unreg: {
    overview: (user: string, coin?: string) => ['unreg-user', 'overview', user, coin] as const,
    workers: (user: string, coin?: string) => ['unreg-user', 'workers', user, coin] as const,
    workerStats: (params: Record<string, any>) => ['unreg-user', 'workers-stats', params] as const,
    hashrateHistory: (params: Record<string, any>) => ['unreg-user', 'hashrate-history', params] as const,
    hashrateChart: (params: Record<string, any>) => ['unreg-user', 'hashrate-chart', params] as const,
    paymentHistory: (params: Record<string, any>) => ['unreg-user', 'payment-history', params] as const,
    rewardHistory: (params: Record<string, any>) => ['unreg-user', 'reward-history', params] as const
  },
  crypto: {
    price: (coin = 'bitcoin') => ['crypto', coin, 'price'] as const,
    prices: (coins: string[], currency: string) => ['crypto', 'prices', coins, currency] as const,
    market: (coins: string[], currency: string) => ['crypto', 'market', coins, currency] as const,
    health: () => ['crypto', 'health'] as const
  },
  worker: {
    overview: (user: string, worker: string, coin?: string) => ['worker', 'overview', user, worker, coin] as const,
    hashrate: (params: Record<string, any>) => ['worker', 'hashrate', params] as const,
    hashrateHistory: (params: Record<string, any>) => ['worker', 'hashrate-history', params] as const
  },
  users: {
    list: (params?: Record<string, any>) => ['users', 'list', params] as const,
    search: (query: string, coin?: string) => ['users', 'search', query, coin] as const
  },
  auth: {
    generate2fa: () => ['auth', '2fa', 'generate'] as const
  }
} as const

/**
 * Performance monitoring hook for query optimization
 * Tracks query execution times and cache hit rates
 */
export const useQueryPerformance = (queryKey: readonly unknown[]) => {
  const startTime = performance.now()
  
  return {
    trackDuration: () => {
      const duration = performance.now() - startTime
      
      // Only log in development
      if (import.meta.env.DEV) {
        console.debug(`Query [${queryKey.join(':')}] took ${duration.toFixed(2)}ms`)
      }
      
      // Could send to analytics in production
      if (duration > 1000) {
        console.warn(`Slow query detected: [${queryKey.join(':')}] - ${duration.toFixed(2)}ms`)
      }
    }
  }
}

/**
 * Automatic query cleanup for memory optimization
 * Removes stale queries after specified timeout
 */
export const useAutoCleanup = (
  queryKeys: readonly (readonly unknown[])[],
  timeout = 10 * 60 * 1000 // 10 minutes default
) => {
  const cleanup = () => {
    queryKeys.forEach(key => {
      const queryData = queryClient.getQueryData(key)
      if (!queryData) {
        queryClient.removeQueries({ queryKey: key })
      }
    })
  }
  
  // Schedule cleanup
  const timer = setTimeout(cleanup, timeout)
  
  return {
    cleanup,
    cancelCleanup: () => clearTimeout(timer)
  }
}

/**
 * Query prefetching strategies for better UX
 * Predictive loading based on user behavior
 */
export const usePrefetchStrategies = () => {
  const prefetchUserDashboard = () => {
    Promise.all([
      queryClient.prefetchQuery({
        queryKey: createQueryKey.user.info(),
        queryFn: () => import('@/api/user').then(m => m.getUserInfo())
      }),
      queryClient.prefetchQuery({
        queryKey: createQueryKey.user.overview(),
        queryFn: () => import('@/api/user').then(m => m.getAccountOverview({}))
      })
    ])
  }
  
  const prefetchPoolData = () => {
    queryClient.prefetchQuery({
      queryKey: createQueryKey.pool.metrics(),
      queryFn: () => import('@/api/stats').then(m => m.getPoolMetrics())
    })
  }
  
  return {
    prefetchUserDashboard,
    prefetchPoolData
  }
}

// Export queryClient for convenience (already imported above) 